import React, { useRef, useState } from 'react'
import styled from 'styled-components'
import { Column, Row } from 'components/common/layout'
import { BaseText } from 'components/common/text'
import { useOnClickOutside } from 'usehooks-ts'
import { DropdownArrowIcon } from 'components/common/icon'

export enum SelectSize {
  DEFAULT = 'default',
  SMALL = 'small',
  EXTRA_SMALL = 'extra-small',
}

const SelectSizeMapping = {
  [SelectSize.DEFAULT]: {
    maxHeight: 134,
    padding: '12px 16px',
    fontSize: '15px',
  },
  [SelectSize.SMALL]: {
    maxHeight: 110,
    padding: '8px 12px',
    fontSize: '14px',
  },
  [SelectSize.EXTRA_SMALL]: {
    maxHeight: 101,
    padding: '6px 10px',
    fontSize: '11px',
  },
}

export const OptionItem = styled(Row)<{ isSelected?: boolean }>`
  color: ${(p) => (p.isSelected ? p.theme.color.black2 : p.theme.color.gray1)};
  ${(p) => p.isSelected && `font-weight: ${p.theme.fontWeight.semiBold};`}
  :not(:last-child) {
    border-bottom: 1px solid ${(p) => p.theme.color.gray2};
  }
  :hover {
    background-color: ${(p) => p.theme.color.background};
  }
  :first-child {
    border-radius: 8px 8px 0 0;
  }
  :last-child {
    border-radius: 0 0 8px 8px;
  }
`

export const Select: React.FCC<{
  value: string | null
  size?: SelectSize
  width?: number
  maxHeight?: number
  isOverflowVisible?: boolean
  autoClose?: boolean
  placeholder?: string
}> = ({
  children,
  value,
  size,
  width,
  maxHeight,
  isOverflowVisible,
  autoClose = true,
  placeholder = '선택',
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const labelRef = useRef<HTMLDivElement>(null)
  const isActive = !!value
  useOnClickOutside(labelRef, () => setIsOpen(false))
  return (
    <Label
      width={width}
      onClick={() => setIsOpen((prev) => !prev)}
      ref={labelRef}
    >
      <Content size={size}>
        <ValueText isActive={isActive} size={size}>
          {isActive ? value : placeholder}
        </ValueText>
        <IconWrapper>
          <DropdownArrowIcon
            size={size === SelectSize.EXTRA_SMALL ? 14 : 20}
            isOpen={isOpen}
          />
        </IconWrapper>
      </Content>
      {isOpen && (
        <OptionContainer
          size={size}
          maxHeight={maxHeight}
          isOverflowVisible={isOverflowVisible}
          onClick={(e) => {
            autoClose && setIsOpen(false)
            e.stopPropagation()
          }}
        >
          {children}
        </OptionContainer>
      )}
    </Label>
  )
}

const IconWrapper = styled.div`
  flex: none;
`

const ValueText = styled(BaseText)<{ isActive: boolean; size?: SelectSize }>`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  ${(p) => !p.isActive && `color: ${p.theme.color.gray2};`}
  font-size: ${(p) => SelectSizeMapping[p.size || SelectSize.DEFAULT].fontSize};
`

const Label = styled(Row)<{ width?: number }>`
  width: ${(p) => (p.width ? p.width + 'px' : '100%')};
  position: relative;
  border-radius: 8px;
  border: 1px solid ${(p) => p.theme.color.gray2};
  background-color: ${(p) => p.theme.color.white1};
  cursor: pointer;
`

const Content = styled(Row)<{ size?: SelectSize }>`
  flex: 1;
  overflow: hidden;
  justify-content: space-between;
  padding: ${(p) => SelectSizeMapping[p.size || SelectSize.DEFAULT].padding};
`

const OptionContainer = styled(Column)<{
  size?: SelectSize
  maxHeight?: number
  isOverflowVisible?: boolean
}>`
  z-index: 1;
  top: 100%;
  width: 100%;
  position: absolute;
  border: 1px solid ${(p) => p.theme.color.gray2};
  border-radius: 8px;
  background-color: ${(p) => p.theme.color.white1};
  filter: drop-shadow(0px 2px 4px rgba(0, 33, 21, 0.19));
  ${(p) =>
    !p.isOverflowVisible &&
    `max-height: ${
      p.maxHeight || SelectSizeMapping[p.size || SelectSize.DEFAULT].maxHeight
    }px; overflow: hidden scroll;`}
  justify-content: start;
  -ms-overflow-style: none;
  scrollbar-width: none;
  ::-webkit-scrollbar {
    display: none;
  }
  ${OptionItem} {
    padding: ${(p) => SelectSizeMapping[p.size || SelectSize.DEFAULT].padding};
    font-size: ${(p) =>
      SelectSizeMapping[p.size || SelectSize.DEFAULT].fontSize};
  }
`
